/**
 * Phase 3 cloud enrollment: flips a cloud (personal) workspace to local-first.
 *
 * enrollCloudWorkspace: one-time — bootstrap-pulls the full server state into
 * SQLite (server uids become the local entity ids), sets the pull cursor,
 * marks the workspace migrated (the router flips to SQLite), enrolls the
 * sync_engine_v1 flag, and starts the engine task. From then on the UI
 * reads/writes SQLite and the Rust engine pushes/pulls in the background.
 *
 * registerEnrolledCloudWorkspaces: on every app start / login — re-registers
 * engine tasks for already-enrolled workspaces (registration is in-memory).
 *
 * Known limitation (recorded in PROGRESS): open tabs referencing the old
 * server integer ids go stale after enrollment (entity ids become uids) —
 * reopen from the sidebar. Inventory item from the phase brief.
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import {
  enrollWorkspaceInSyncEngine,
  syncEngineEnabled,
  syncEngineKilled,
} from '../utils/featureFlags';
import { isBackendWorkspace } from '../utils/workspaceStorage';
import { recordFeature, ACTION } from './telemetry';

export async function enrollCloudWorkspace(workspace, { isAuthenticated } = {}) {
  if (!isTauri() || syncEngineKilled()) {
    throw new Error('Sync engine unavailable (desktop only / kill switch on)');
  }
  if (!isBackendWorkspace(workspace, isAuthenticated ?? true)) {
    throw new Error('Only cloud workspaces can be enrolled this way');
  }
  const workspaceId = String(workspace.id);
  const counts = await invoke('sync_bootstrap_cloud', {
    workspaceId,
    serverWorkspaceId: workspaceId,
    // Team mode (Phase 5): faster push cadence; deltas arrive over the
    // channel and reconnects gap-fill with pulls.
    mode: workspace?.type === 'team' ? 'team' : 'cloud',
  });
  enrollWorkspaceInSyncEngine(workspaceId);
  console.info(`[sync] cloud workspace ${workspaceId} enrolled; bootstrap counts:`, counts);
  return counts;
}

/** Re-arm engine tasks for already-enrolled cloud workspaces (idempotent). */
export async function registerEnrolledCloudWorkspaces(workspaces, isAuthenticated) {
  if (!isTauri() || syncEngineKilled() || !isAuthenticated) return;
  for (const ws of workspaces || []) {
    if (!syncEngineEnabled(ws)) continue;
    if (!isBackendWorkspace(ws, isAuthenticated)) continue;
    const workspaceId = String(ws.id);
    try {
      const migrated = await invoke('workspace_db_is_migrated', { workspaceId });
      if (migrated) {
        await invoke('sync_register_workspace', {
          workspaceId,
          serverWorkspaceId: workspaceId,
          mode: ws.type === 'team' ? 'team' : 'cloud',
        });
        recordFeature(ACTION.SYNC_ON);   // identifier-free; once a day at most   // identifier-free; once a day at most
      }
    } catch (err) {
      console.warn(`[sync] engine registration failed for ${workspaceId}:`, err);
    }
  }
}
